import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection, 
  addDoc,
  serverTimestamp,
  increment,
  runTransaction,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from 'firebase/firestore';
import { getVerificationTier, validateXPTransaction, VERIFICATION_TIERS } from '../utils/xp';
import logger from '../utils/logger';

// User XP and verification management service
class UserService {
  
  // Initialize user with XP system
  async initializeUser(userId, userData = {}) {
    const userRef = doc(db, 'users', userId);
    
    const defaultUserData = {
      xp: 0,
      verificationTier: 'none',
      dailyPostCount: 0,
      lastPostDate: null,
      joinedAt: serverTimestamp(),
      isManuallyVerified: false,
      manualVerificationReason: null,
      ...userData
    };
    
    try {
      await setDoc(userRef, defaultUserData, { merge: true });
      return defaultUserData;
    } catch (error) {
      logger.error('Error initializing user:', error);
      throw error;
    }
  }
  
  // Get user with XP data
  async getUser(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        // Calculate current verification tier based on XP
        const currentTier = getVerificationTier(userData.xp || 0);
        
        return {
          id: userId,
          ...userData,
          calculatedTier: currentTier,
          // Use manual verification if set, otherwise use calculated tier
          effectiveTier: userData.isManuallyVerified ? userData.verificationTier : currentTier.id
        };
      }
      
      return null;
    } catch (error) {
      logger.error('Error getting user:', error);
      throw error;
    }
  }
  
  // Award XP to user (with transaction for consistency)
  async awardXP(userId, xpAmount, reason, sourcePostId = null) {
    // Check for active XP boost multiplier
    const { default: tokenBurnService } = await import('./tokenBurnService');
    const boostMultiplier = await tokenBurnService.getActiveBoostMultiplier(userId);

    // Apply boost multiplier
    const boostedXP = Math.round(xpAmount * boostMultiplier);
    const actualXPAwarded = boostedXP;

    const transaction = {
      userId,
      xpAmount: actualXPAwarded,
      baseXP: xpAmount,
      boostMultiplier,
      reason: boostMultiplier > 1 ? `${reason} (${boostMultiplier}x boost)` : reason,
      sourcePost: sourcePostId,
      awardedAt: serverTimestamp()
    };

    if (!validateXPTransaction(transaction)) {
      throw new Error('Invalid XP transaction data');
    }

    let hasRankUp = false;

    try {
      await runTransaction(db, async (firestoreTransaction) => {
        const userRef = doc(db, 'users', userId);
        const userDoc = await firestoreTransaction.get(userRef);

        if (!userDoc.exists()) {
          // Initialize user if doesn't exist
          await this.initializeUser(userId);
        }

        const currentData = userDoc.data() || {};
        const oldXP = currentData.xp || 0;
        const newXP = oldXP + actualXPAwarded;
        const oldTier = getVerificationTier(oldXP);
        const newTier = getVerificationTier(newXP);
        
        // Check for rank up
        hasRankUp = oldTier.id !== newTier.id && !currentData.isManuallyVerified;
        
        // Update user XP and tier
        const updateData = {
          xp: newXP,
          verificationTier: currentData.isManuallyVerified ? currentData.verificationTier : newTier.id,
          lastXPUpdate: serverTimestamp()
        };
        
        // If rank up detected, mark as pending unlock
        if (hasRankUp) {
          updateData.pendingRankUp = {
            oldTier: oldTier.id,
            newTier: newTier.id,
            unlockedAt: null,
            triggeredAt: serverTimestamp()
          };
        }
        
        firestoreTransaction.update(userRef, updateData);
        
        // Log XP transaction
        const xpTransactionRef = doc(collection(db, 'xpTransactions'));
        firestoreTransaction.set(xpTransactionRef, {
          ...transaction,
          newTotalXP: newXP,
          newTier: newTier.id,
          triggeredRankUp: hasRankUp
        });
        
        // Create rank up notification if tier changed
        if (hasRankUp) {
          const notificationRef = doc(collection(db, 'notifications'));
          firestoreTransaction.set(notificationRef, {
            userId,
            type: 'level_up',
            title: `🎉 Rank Up! You're now ${newTier.name}!`,
            message: `You've earned enough XP to unlock ${newTier.name} tier! Click Rank Up to claim your new rank and benefits.`,
            fromTier: oldTier.name,
            toTier: newTier.name,
            newTierBadge: newTier.badge,
            read: false,
            createdAt: serverTimestamp(),
            actionRequired: true,
            actionType: 'level_up_unlock'
          });
        }
      });
      
      return { success: true, levelUp: hasRankUp };
    } catch (error) {
      logger.error('Error awarding XP:', error);
      throw error;
    }
  }
  
  // Unlock pending rank up (user clicks rank up tab)
  async unlockRankUp(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }
      
      const userData = userDoc.data();
      const pendingRankUp = userData.pendingRankUp;
      
      if (!pendingRankUp || pendingRankUp.unlockedAt) {
        return { success: false, message: 'No pending rank up to unlock' };
      }
      
      // Mark rank up as unlocked
      await updateDoc(userRef, {
        'pendingRankUp.unlockedAt': serverTimestamp(),
        lastRankUpUnlock: serverTimestamp()
      });
      
      // Mark rank up notification as read
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('type', '==', 'level_up'),
        where('actionRequired', '==', true),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const notificationDocs = await getDocs(notificationsQuery);
      if (!notificationDocs.empty) {
        const notificationDoc = notificationDocs.docs[0];
        await updateDoc(doc(db, 'notifications', notificationDoc.id), {
          read: true,
          actionRequired: false,
          unlockedAt: serverTimestamp()
        });
      }
      
      return { 
        success: true, 
        message: `🎉 Welcome to ${pendingRankUp.newTier} tier!`,
        newTier: pendingRankUp.newTier
      };
    } catch (error) {
      logger.error('Error unlocking rank up:', error);
      throw error;
    }
  }
  
  // Check if user has pending rank up
  async hasPendingRankUp(userId) {
    try {
      const user = await this.getUser(userId);
      if (!user || !user.pendingRankUp) {
        return false;
      }
      
      return !user.pendingRankUp.unlockedAt;
    } catch (error) {
      logger.error('Error checking pending rank up:', error);
      return false;
    }
  }
  
  // Manually set verification tier (admin function)
  async setManualVerification(userId, tierLevel, reason, adminUserId) {
    try {
      const userRef = doc(db, 'users', userId);
      
      await updateDoc(userRef, {
        isManuallyVerified: true,
        verificationTier: tierLevel,
        manualVerificationReason: reason,
        manuallyVerifiedAt: serverTimestamp(),
        manuallyVerifiedBy: adminUserId
      });
      
      // Log manual verification
      await addDoc(collection(db, 'manualVerifications'), {
        userId,
        tierLevel,
        reason,
        adminUserId,
        timestamp: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      logger.error('Error setting manual verification:', error);
      throw error;
    }
  }
  
  // Remove manual verification (revert to XP-based)
  async removeManualVerification(userId, adminUserId) {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const calculatedTier = getVerificationTier(userData.xp || 0);
        
        await updateDoc(userRef, {
          isManuallyVerified: false,
          verificationTier: calculatedTier.id,
          manualVerificationReason: null,
          manualVerificationRemovedAt: serverTimestamp(),
          manualVerificationRemovedBy: adminUserId
        });
      }
      
      return true;
    } catch (error) {
      logger.error('Error removing manual verification:', error);
      throw error;
    }
  }
  
  // Check if user can post (daily limits)
  async canUserPost(userId) {
    try {
      const user = await this.getUser(userId);
      if (!user) return { canPost: false, reason: 'User not found' };
      
      const today = new Date().toDateString();
      const lastPostDate = user.lastPostDate?.toDate?.()?.toDateString();
      
      // Reset daily count if it's a new day
      const dailyCount = lastPostDate === today ? (user.dailyPostCount || 0) : 0;
      
      const tier = getVerificationTier(user.xp || 0);
      const effectiveTier = user.isManuallyVerified ? 
        Object.values(VERIFICATION_TIERS).find(t => t.id === user.verificationTier) || tier :
        tier;
      
      if (effectiveTier.postLimit === -1) {
        return { canPost: true, remaining: -1 };
      }
      
      const remaining = effectiveTier.postLimit - dailyCount;
      return {
        canPost: remaining > 0,
        remaining: Math.max(0, remaining),
        tier: effectiveTier.name
      };
    } catch (error) {
      logger.error('Error checking post permission:', error);
      return { canPost: false, reason: 'Error checking permissions' };
    }
  }
  
  // Increment daily post count
  async incrementPostCount(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      const today = new Date().toDateString();
      
      await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userRef);
        const userData = userDoc.data() || {};
        
        const lastPostDate = userData.lastPostDate?.toDate?.()?.toDateString();
        const isNewDay = lastPostDate !== today;
        
        transaction.update(userRef, {
          dailyPostCount: isNewDay ? 1 : increment(1),
          lastPostDate: serverTimestamp()
        });
      });
      
      return true;
    } catch (error) {
      logger.error('Error incrementing post count:', error);
      throw error;
    }
  }
  
  // Get user's XP transactions history
  async getUserXPHistory(userId, limit = 50) {
    try {
      const q = query(
        collection(db, 'xpTransactions'),
        where('userId', '==', userId),
        orderBy('awardedAt', 'desc'),
        limit(limit)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      logger.error('Error getting XP history:', error);
      throw error;
    }
  }
}

export default new UserService();